/** @format */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  HomefieldIconsLibraryComponent,
  HomefieldIconsLibraryService,
  homefieldIconArrowSwapVerticalOutline,
  homefieldIconListFilterOutline,
} from '@dsg-tech/homefield-angular-icons';
import {
  selectFeatureFlags,
  selectSelectedFilters,
  selectSkeletonLoader,
  selectValidFacetVOs,
} from '@plp/data-access/state';

import { CarouselButtonComponent } from '@shared/components';
import { CompareBarComponent } from '../compare-bar/compare-bar.component';
import { ExposedFiltersComponent } from '../exposed-filters/exposed-filters.component';
import { FacetVO } from '@plp/data-access/interfaces';
import { FilterComponent } from '../filter/filter.component';
import { HmfPopoverComponent } from '@dsg-tech/homefield-angular';
import { ProductListFilterDrawerService } from '@plp/data-access/services';
import { ShippingOptionsComponent } from '../shipping-options/shipping-options.component';
import { SortComponent } from '../sort/sort.component';
import { Store } from '@ngrx/store';
import { fromEvent } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { selectCompareBarProducts } from '@plp/data-access/compare';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'plp-components-filter-bar',
  imports: [
    ExposedFiltersComponent,
    ShippingOptionsComponent,
    SortComponent,
    HomefieldIconsLibraryComponent,
    CompareBarComponent,
    CarouselButtonComponent,
    HmfPopoverComponent,
    FilterComponent,
  ],
  templateUrl: './filter-bar.component.html',
  styleUrl: './filter-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterBarComponent {
  private readonly store = inject(Store);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterDrawerService = inject(ProductListFilterDrawerService);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

  private readonly featureFlags = this.store.selectSignal(selectFeatureFlags);
  private readonly selectedFilters = this.store.selectSignal(selectSelectedFilters);
  readonly compareBarProducts = this.store.selectSignal(selectCompareBarProducts);
  readonly isDisabled = this.store.selectSignal(selectSkeletonLoader);
  readonly productListFacetVOs = this.store.selectSignal(selectValidFacetVOs);

  readonly isMobile = input<boolean>(false);
  readonly isPlp3Template = input<boolean>(false);
  readonly skipLinkOpen = output<void>();

  readonly exposedFilterPopoverOpen = signal(false);
  readonly activeExposedFilter = signal<FacetVO | null>(null);
  readonly activeExposedFilterAnchor = signal<HTMLElement | null>(null);
  readonly activeExposedFilterFocusAttrIdentifier = signal<string | null>(null);
  readonly refinementSource = 'DynamicExposed' as const;

  readonly exposedFilters = computed(() => {
    const facetVOs = this.productListFacetVOs();
    if (facetVOs?.length) {
      return facetVOs
        .filter((facet) => facet.relevancyScore)
        .sort((a, b) => (b.relevancyScore ?? 0) - (a.relevancyScore ?? 0));
    }

    return [];
  });

  readonly isFulfillmentFiltersSelected = computed(() => {
    return !!this.selectedFilters()['facetStore']?.length;
  });

  readonly selectedFilterCount = computed(() => {
    const filters = this.selectedFilters();
    let count = 0;
    for (const key in filters) {
      if (filters[key]?.length) {
        count += filters[key].length;
      }
    }
    return count;
  });

  readonly exposedFiltersEnabled = computed(() => this.featureFlags().exposedFiltersEnabled);

  readonly moveFulfillmentOptions = computed(
    () => this.featureFlags().movefulfillmentOptionsToFilters,
  );

  readonly exposedFiltersStickyOnMobile = computed(
    () => this.featureFlags().exposedFiltersStickyOnMobile,
  );

  readonly exposedFiltersSticky = computed(() => {
    return (
      this.exposedFiltersEnabled() &&
      this.exposedFilters()?.length > 0 &&
      this.exposedFiltersStickyOnMobile()
    );
  });

  readonly sortByCustomerRatingEnabled = computed(
    () => this.featureFlags().sortByCustomerRatingEnabled,
  );

  readonly compareCheckboxEnabled = computed(() => this.featureFlags().compareCheckboxEnabled);
  readonly isFilterContainerSticky = signal<boolean>(false);

  /**
   * Reference to the scrollable area element for shipping filters.
   */
  readonly filtersShippingScrollableArea = viewChild<ElementRef<HTMLDivElement>>(
    'filtersShippingScrollableArea',
  );

  private readonly mobileShippingFilterRef = viewChild('mobileShippingFilter', {
    read: ElementRef,
  });

  private readonly desktopTopBarContainerRef = viewChild('desktopTopBarContainer', {
    read: ElementRef,
  });

  private readonly compareBarMobileRef = viewChild('compareBarMobile', { read: ElementRef });

  private readonly compareBarDesktopContainerRef = viewChild('compareBarDesktopContainer', {
    read: ElementRef,
  });

  private readonly desktopExposedFilterContentRef = viewChild<ElementRef<HTMLDivElement>>(
    'desktopExposedFilterContent',
  );

  private readonly focusableElementSelector =
    'input:not([disabled]), button:not([disabled]), a[href], [tabindex]';

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(
      container.querySelectorAll<HTMLElement>(this.focusableElementSelector),
    ).filter((el) => !el.closest('[inert]') && el.tabIndex >= 0);
  }

  private readonly chipControlSelector = '[tabindex]';

  private readonly scrollState = signal({
    left: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });

  readonly showLeftArrow = computed(() => {
    const state = this.scrollState();
    return state.left > 0;
  });

  readonly showRightArrow = computed(() => {
    const state = this.scrollState();
    return state.left + state.clientWidth < state.scrollWidth - 1;
  });

  private resizeObserver: ResizeObserver | null = null;
  private animationFrameId: number | null = null;
  private stickyFrameId: number | null = null;
  private modifiedChipAttrIdentifiers: string[] = [];

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconListFilterOutline,
      homefieldIconArrowSwapVerticalOutline,
    ]);

    afterNextRender(() => {
      this.updateScrollState();
      this.setupResizeObserver();
      this.styleStickyFilterContainer();
      this.setupScrollListener();
    });

    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }
      if (this.stickyFrameId !== null) {
        cancelAnimationFrame(this.stickyFrameId);
      }
    });
  }

  private setupScrollListener(): void {
    fromEvent(globalThis, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.stickyFrameId !== null) {
          cancelAnimationFrame(this.stickyFrameId);
        }
        this.stickyFrameId = requestAnimationFrame(() => {
          this.stickyFrameId = null;
          this.styleStickyFilterContainer();
        });
      });
  }

  /**
   * Scrolls the filters container horizontally by 30% of its visible width in the specified direction.
   *
   * This method calculates the scroll step based on the container's width and scrolls either left or right.
   * It ensures the scroll position stays within valid bounds and updates the scroll state accordingly.
   * After scrolling, it triggers an update to the scroll state after a short delay.
   */
  scrollFilters(direction: 'left' | 'right'): void {
    this.closeExposedFilterPopover();
    const filtersContainerElem = this.filtersShippingScrollableArea()?.nativeElement;
    if (!filtersContainerElem) return;

    // Scroll by 30% of the container's width
    const scrollStep = Math.floor(filtersContainerElem.clientWidth * 0.3);
    const scrollAmount = direction === 'right' ? scrollStep : -scrollStep;
    const maxScroll = filtersContainerElem.scrollWidth - filtersContainerElem.clientWidth;
    const newScrollPosition = filtersContainerElem.scrollLeft + scrollAmount;
    const targetScrollPosition = Math.min(Math.max(newScrollPosition, 0), maxScroll);
    filtersContainerElem.scrollTo({ left: targetScrollPosition, behavior: 'smooth' });
  }

  private updateScrollState(): void {
    const filtersContainerElem = this.filtersShippingScrollableArea()?.nativeElement;
    if (!filtersContainerElem) return;

    this.scrollState.set({
      left: filtersContainerElem.scrollLeft,
      scrollWidth: filtersContainerElem.scrollWidth,
      clientWidth: filtersContainerElem.clientWidth,
    });
  }

  // Setup resize observer for scroll state updates
  private setupResizeObserver(): void {
    if (isPlatformServer(this.platformId)) return;

    const filtersContainerElem = this.filtersShippingScrollableArea()?.nativeElement;
    if (!filtersContainerElem) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollState();
    });

    this.resizeObserver.observe(filtersContainerElem);
  }

  onFilterScroll(): void {
    this.closeExposedFilterPopover();
    if (!this.featureFlags().exposedFiltersEnabled) return;

    // Used requestAnimationFrame to limit the number of times updateScrollState is called during scrolling
    // This improves performance by batching updates to the next animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
    }

    this.animationFrameId = requestAnimationFrame(() => {
      this.updateScrollState();
    });
  }

  /**
   * Styles the sticky filter container based on certain conditions.
   * Writes top and transition directly to the DOM element to avoid Angular CD
   * scheduling delay, ensuring the filter bar stays in sync with the header.
   */
  private styleStickyFilterContainer(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const stickyFilterContainer = this.isMobile()
      ? this.mobileShippingFilterRef()?.nativeElement
      : this.desktopTopBarContainerRef()?.nativeElement;

    if (!stickyFilterContainer) {
      return;
    }

    const filterRect = stickyFilterContainer.getBoundingClientRect();
    const stickySearchBar = document.querySelector('.dsg-header-search-sticky-enabled.sticky');
    const isSticky = !!stickySearchBar;
    const headerHeight = stickySearchBar?.getBoundingClientRect().height ?? 0;

    // Write transition and top directly
    const transitionValue = isSticky && this.isMobile() ? 'top var(--hmf-fast) ease-in' : 'none';
    stickyFilterContainer.style.transition = transitionValue;
    stickyFilterContainer.style.top = `${headerHeight}px`;

    const compareBarElem = this.isMobile()
      ? this.compareBarMobileRef()?.nativeElement
      : this.compareBarDesktopContainerRef()?.nativeElement;
    if (compareBarElem) {
      compareBarElem.style.transition = transitionValue;
      compareBarElem.style.top = `${filterRect.height + headerHeight}px`;
    }
    this.isFilterContainerSticky.set(filterRect.top <= headerHeight);
  }

  protected trackFilterName(index: number, facetVO: FacetVO): string {
    return index.toString() + facetVO.attrName;
  }

  openFilterDrawer(): void {
    this.filterDrawerService.open();
  }

  handleFocusOut(): void {
    this.skipLinkOpen.emit();
  }

  openExposedFilterPopover(event: { anchorElement: HTMLElement; filter: FacetVO }): void {
    this.restoreChipTabbing();
    const currentFilter = this.activeExposedFilter();
    const isSameFilter = currentFilter?.attrIdentifier === event.filter.attrIdentifier;

    if (isSameFilter && this.exposedFilterPopoverOpen()) {
      this.closeExposedFilterPopover();
      return;
    }

    this.activeExposedFilter.set(event.filter);
    this.activeExposedFilterAnchor.set(event.anchorElement);
    this.activeExposedFilterFocusAttrIdentifier.set(event.filter.attrIdentifier);
    this.exposedFilterPopoverOpen.set(true);
    this.disableInactiveChipTabbing();
  }

  closeExposedFilterPopover(): void {
    this.restoreChipTabbing();
    this.exposedFilterPopoverOpen.set(false);
    this.activeExposedFilter.set(null);
    this.activeExposedFilterAnchor.set(null);
    this.activeExposedFilterFocusAttrIdentifier.set(null);
  }

  protected isExposedFilterOpen(filter: FacetVO): boolean {
    return (
      this.exposedFilterPopoverOpen() &&
      this.activeExposedFilter()?.attrIdentifier === filter.attrIdentifier
    );
  }

  handleExposedFilterContentTab(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    const contentElement = this.desktopExposedFilterContentRef()?.nativeElement;
    if (!contentElement) return;

    const focusableElements = this.getFocusableElements(contentElement);

    if (!focusableElements.length) return;

    const activeElement = document.activeElement;

    if (keyboardEvent.shiftKey) {
      const isOnFirst =
        focusableElements[0] === activeElement || focusableElements[0].contains(activeElement);
      if (!isOnFirst) return;

      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      const focusTargetAttrIdentifier = this.activeExposedFilterFocusAttrIdentifier();
      this.closeExposedFilterPopover();
      if (!focusTargetAttrIdentifier) {
        return;
      }

      const focusTarget = this.getTabbableChipElement(focusTargetAttrIdentifier);
      if (!focusTarget?.isConnected) {
        return;
      }

      focusTarget.focus({ preventScroll: true });
    } else {
      const lastIndex = focusableElements.length - 1;
      const currentIndex = focusableElements.findIndex(
        (el) => el === activeElement || el.contains(activeElement),
      );
      if (currentIndex !== lastIndex) return;

      const nextFilter = this.getNextExposedFilter();
      if (!nextFilter) return;

      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();

      // Restore chip tabbing and focus next chip BEFORE closing popover,
      // so the chip is tabbable and can receive focus immediately
      this.restoreChipTabbing();
      this.closeExposedFilterPopover();
      this.focusExposedFilterChip(nextFilter.attrIdentifier);
    }
  }

  focusActiveExposedFilterContent(): void {
    const contentElement = this.desktopExposedFilterContentRef()?.nativeElement;
    if (!contentElement) {
      return;
    }

    const [firstFocusableElement] = this.getFocusableElements(contentElement);
    firstFocusableElement?.focus({ preventScroll: true });
  }

  private getNextExposedFilter(): FacetVO | null {
    const activeFilter = this.activeExposedFilter();
    if (!activeFilter) {
      return null;
    }

    const filters = this.exposedFilters();
    const activeIndex = filters.findIndex(
      (filter) => filter.attrIdentifier === activeFilter.attrIdentifier,
    );

    if (activeIndex === -1 || activeIndex === filters.length - 1) {
      return null;
    }

    return filters[activeIndex + 1];
  }

  private getChipControlElement(attrIdentifier: string): HTMLElement | null {
    const chipHost = document.getElementById(`chip-filter-${attrIdentifier}`);
    return chipHost?.querySelector<HTMLElement>(this.chipControlSelector) ?? null;
  }

  private getTabbableChipElement(attrIdentifier: string): HTMLElement | null {
    const chipElement = this.getChipControlElement(attrIdentifier);
    if (!chipElement || chipElement.tabIndex < 0) {
      return null;
    }

    return chipElement;
  }

  private disableInactiveChipTabbing(): void {
    const activeFilter = this.activeExposedFilter();
    if (!activeFilter) return;

    const filters = this.exposedFilters();
    const activeIndex = filters.findIndex((f) => f.attrIdentifier === activeFilter.attrIdentifier);

    for (let i = activeIndex + 1; i < filters.length; i++) {
      const chipControl = this.getChipControlElement(filters[i].attrIdentifier);
      if (chipControl) {
        chipControl.setAttribute('tabindex', '-1');
        this.modifiedChipAttrIdentifiers.push(filters[i].attrIdentifier);
      }
    }
  }

  private restoreChipTabbing(): void {
    for (const attrIdentifier of this.modifiedChipAttrIdentifiers) {
      const chipControl = this.getChipControlElement(attrIdentifier);
      if (chipControl) {
        chipControl.setAttribute('tabindex', '0');
      }
    }

    this.modifiedChipAttrIdentifiers = [];
  }

  private focusExposedFilterChip(attrIdentifier: string): void {
    const chipFocusableElement = this.getTabbableChipElement(attrIdentifier);
    chipFocusableElement?.focus({ preventScroll: true });
  }
}
